import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { isoDaysAgo, today } from "@/lib/format";
import { BrainCircuit, Loader2, TrendingUp, AlertTriangle, Radar, Lightbulb, Activity } from "lucide-react";

function Gauge({ score }) {
  const color = score >= 70 ? "#A3E635" : score >= 45 ? "#F59E0B" : "#F43F5E";
  const circ = 2 * Math.PI * 52;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="relative h-36 w-36">
      <svg className="h-36 w-36 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r="52" fill="none" stroke="#242B30" strokeWidth="10" />
        <circle cx="60" cy="60" r="52" fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-3xl font-extrabold text-white">{score}</span>
        <span className="text-[10px] font-mono uppercase text-zinc-500">Health</span>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, color, items, render }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4" style={{ color }} />
        <h3 className="font-display font-semibold text-white">{title}</h3>
      </div>
      <div className="space-y-3">{items.map(render)}</div>
    </div>
  );
}

export default function Analyst() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyze = async () => {
    setLoading(true); setResult(null);
    try {
      const res = await api.post("/ai/analyst", null, { params: { start: isoDaysAgo(29), end: today() } });
      setResult(res.data.analysis);
      toast.success("Analysis complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-4 sm:p-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="flex items-start gap-3 max-w-2xl">
          <div className="h-10 w-10 rounded-lg bg-lime-400/10 border border-lime-400/30 flex items-center justify-center shrink-0">
            <BrainCircuit className="h-5 w-5 text-lime-400" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-white">Evidence-based performance analysis</h3>
            <p className="text-sm text-zinc-400 mt-1">Analyzes your last 30 days. Every recommendation cites the numbers behind it — no invented metrics, no causation claims from correlation.</p>
          </div>
        </div>
        <button data-testid="analyze-btn" onClick={analyze} disabled={loading}
          className="w-full lg:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-lime-400 hover:bg-lime-300 px-6 min-h-[48px] text-sm font-semibold text-[#0A0D0E] disabled:opacity-60 shrink-0">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />}
          {loading ? "Analyzing…" : "Run Analysis"}
        </button>
      </div>

      {loading && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 text-lime-400 animate-spin" /></div>}

      {result && (
        <div className="space-y-6">
          <div className="bg-[#14181B] border border-[#242B30] rounded-xl p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
            <Gauge score={result.health_score || 0} />
            <p className="text-zinc-200 leading-relaxed flex-1 text-sm sm:text-base">{result.summary}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Section icon={TrendingUp} title="Trends" color="#06B6D4" items={result.trends} render={(t, i) => (
              <div key={i} className="bg-[#0F1315] border border-[#242B30] rounded-lg p-3">
                <p className="text-sm text-zinc-200">{t.observation}</p>
                <p className="text-[11px] text-cyan-400/80 font-mono mt-1">Evidence: {t.evidence}</p>
              </div>
            )} />
            <Section icon={AlertTriangle} title="Creative Fatigue Signals" color="#F59E0B" items={result.fatigue_signals} render={(t, i) => (
              <div key={i} className="bg-[#0F1315] border border-[#242B30] rounded-lg p-3">
                <p className="text-sm text-zinc-200">{t.signal}</p>
                <p className="text-[11px] text-amber-400/80 font-mono mt-1">Evidence: {t.evidence}</p>
              </div>
            )} />
            <Section icon={Radar} title="Possible Tracking Flags" color="#F43F5E" items={result.tracking_flags} render={(t, i) => (
              <div key={i} className="bg-[#0F1315] border border-[#242B30] rounded-lg p-3">
                <p className="text-sm text-zinc-200">{t.flag}</p>
                <p className="text-[11px] text-rose-400/80 font-mono mt-1">Evidence: {t.evidence}</p>
              </div>
            )} />
            <Section icon={Lightbulb} title="Recommendations & Tests" color="#A3E635" items={result.recommendations} render={(t, i) => (
              <div key={i} className="bg-[#0F1315] border border-lime-400/20 rounded-lg p-3">
                <p className="text-sm text-white font-medium">{t.action}</p>
                <p className="text-[11px] text-zinc-400 font-mono mt-1">Evidence: {t.evidence}</p>
                {t.test && <p className="text-[11px] text-lime-400/90 font-mono mt-1">Test: {t.test}</p>}
              </div>
            )} />
          </div>
        </div>
      )}
    </div>
  );
}
